import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { FOCUS_RING } from "./tokens";
import Button from "./Button";

/**
 * Filtros das listagens em telas estreitas.
 *
 * No celular os filtros empilhavam: em Clientes eram cinco selects ocupando
 * ~250px antes de qualquer resultado — 30% da altura útil gasta antes do
 * conteúdo. Aqui viram uma folha que sobe sob demanda, e o botão mostra quantos
 * estão aplicados, para o filtro esquecido não passar despercebido.
 *
 * Acima de `md` nada disso aparece: os filtros seguem em linha, como estão.
 */
export default function FilterSheet({ count = 0, onClear, children, className }) {
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("md:hidden", className)}>
      <Dialog.Root open={open} onOpenChange={setOpen}>
        <Dialog.Trigger asChild>
          <button
            type="button"
            data-testid="filter-sheet-trigger"
            data-ring=""
            className={cn(
              "inline-flex items-center gap-2 h-10 px-3.5 rounded-lg border bg-white text-sm font-semibold transition-colors",
              count > 0 ? "border-brand-300 text-brand-700" : "border-slate-200 text-slate-600",
              FOCUS_RING,
            )}
          >
            <SlidersHorizontal size={16} aria-hidden />
            Filtros
            {count > 0 && (
              <span className="ml-0.5 min-w-5 h-5 px-1.5 rounded-full bg-brand-600 text-white text-xs font-bold grid place-items-center">
                {count}
              </span>
            )}
          </button>
        </Dialog.Trigger>

        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-slate-900/40" />
          {/* Sobe de baixo: é onde o polegar alcança. */}
          <Dialog.Content
            data-testid="filter-sheet"
            className="fixed inset-x-0 bottom-0 z-50 max-h-[85vh] flex flex-col rounded-t-2xl border-t border-slate-200 bg-white focus:outline-none"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 shrink-0">
              <Dialog.Title className="font-heading font-semibold text-slate-900">Filtros</Dialog.Title>
              <Dialog.Close asChild>
                <Button variant="ghost" size="iconSm" aria-label="Fechar filtros"><X size={18} /></Button>
              </Dialog.Close>
            </div>

            {/* Estica os controles na vertical. Precisa de `!` porque os mesmos
                selects levam `w-auto` (e os Autocomplete, `w-48`/`w-56`) para a
                linha do desktop — sem o important dá empate de especificidade e
                a folha fica com larguras irregulares. */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-3 [&>div]:!w-full [&_select]:!w-full [&_input]:!w-full">
              {children}
            </div>

            <div className="flex gap-2 p-4 border-t border-slate-100 shrink-0">
              {count > 0 && onClear && (
                <Button variant="secondary" size="lg" block onClick={() => { onClear(); setOpen(false); }} data-testid="filter-sheet-clear">
                  Limpar
                </Button>
              )}
              <Button size="lg" block onClick={() => setOpen(false)} data-testid="filter-sheet-apply">
                Ver resultados
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
